/**
 * Rules (spec §2): trump ladder, bidding, scoring, timers. Plain talk, table-side.
 *
 * The timer numbers are read from `@spades/shared` rather than written out here. They have now been
 * retuned twice on playtest feedback, and both times this sheet was left quoting the old ones —
 * which is worse than saying nothing, because a player who reads it and then watches a different
 * clock stops trusting the rest of the page.
 */
import { BLITZ_TIMERS, HOUSE_RULES } from '@spades/shared';
import { Sheet } from '../ui';

/** Whole seconds, because nobody reads a rules sheet in milliseconds. */
const secs = (ms: number): string => `${Math.round(ms / 1000)}s`;

export function RulesContent() {
  const t = HOUSE_RULES.timers;
  return (
    <div className="prose" data-testid="rules-content">
      <h2>The deck</h2>
      <p>52 cards: the 2♥ and 2♣ sit out, both jokers come in. Spades are trump, and the four cards above them are trump too.</p>
      <div className="ladder" aria-label="Trump order, high to low">
        <span className="ladder__card ladder__card--joker">Big Joker</span>
        <span className="ladder__card ladder__card--joker">Little Joker</span>
        <span className="ladder__card ladder__card--red">2♦</span>
        <span className="ladder__card">2♠</span>
        <span className="ladder__card">A♠</span>
        <span className="ladder__card">K♠</span>
        <span className="ladder__card">…</span>
        <span className="ladder__card">3♠</span>
      </div>
      <p>Four seats, two teams, partners sit across. Thirteen books a hand.</p>

      <h2>Bidding</h2>
      <p>
        Everybody bids at once, in secret, on one 20-second clock. Drag your stack up to the number of books you are promising. Nobody sees a
        number until all four chips lock, then they flip together.
      </p>
      <ul>
        <li>Your bid plus your partner&apos;s is the team contract.</li>
        <li>
          <strong>Nil</strong> is zero: win no books for +100, catch one and it&apos;s −100. Your partner&apos;s books still count for the team.
        </li>
        <li>
          <strong>Blind nil</strong> is offered only when you trail by 100 or more. Bid it before you look: ±200. Peek and the offer is gone.
        </li>
        <li>
          <strong>Board</strong>: a team contract under 4 rounds up to 4.
        </li>
        <li>Miss the clock and you get the bot&apos;s suggestion, never a random number.</li>
      </ul>

      <h2>Play</h2>
      <p>
        Follow the suit led if you can. No cards of that suit? Cut with a spade or a joker. Trumps take the trick; the highest trump wins.
        Cards you can&apos;t play are dimmed, so a renege is impossible here.
      </p>
      <p>
        <strong>Lock a card early.</strong> When it isn&apos;t your turn, drag a card to the table and it waits face down with a gold rim. It fires the
        instant your turn comes. If the led suit makes it illegal, it bounces back and you pick again.
      </p>

      <h2>Scoring</h2>
      <table>
        <tbody>
          <tr>
            <td>Contract made</td>
            <td>bid × 10, +1 per extra book (a bag)</td>
          </tr>
          <tr>
            <td>Set (contract missed)</td>
            <td>−(bid × 10)</td>
          </tr>
          <tr>
            <td>Ten bags</td>
            <td>−100, counter resets</td>
          </tr>
          <tr>
            <td>Nil / Blind nil</td>
            <td>±100 / ±200</td>
          </tr>
          <tr>
            <td>Boston (all 13 books)</td>
            <td>+200 and the whole room hears about it</td>
          </tr>
        </tbody>
      </table>

      <h2>The match</h2>
      <p>
        <strong>Quick match</strong>: four hands, every seat deals once, highest total wins. A tie goes to one sudden-death hand.{' '}
        <strong>Classic</strong> (private tables): first team to 500, or 300. <strong>Money Hand</strong>: one hand, winner takes it, offered on
        every rematch screen.
      </p>

      <h2>Timers</h2>
      <table>
        <tbody>
          <tr>
            <td>Bid</td>
            <td>{secs(t.bidMs)}, everybody at once</td>
          </tr>
          <tr>
            <td>Play</td>
            <td>
              {secs(t.playMs)} ({secs(t.forcedMs)} if you only have one legal card)
            </td>
          </tr>
          <tr>
            <td>Time bank</td>
            <td>
              {t.timeBanksPerHand} × {secs(t.timeBankMs)} each hand. If your clock runs out you spend one automatically and
              keep the turn; only a seat with none left gets played for.
            </td>
          </tr>
          <tr>
            <td>Blitz (private)</td>
            <td>
              {secs(BLITZ_TIMERS.bidMs)} bid, {secs(BLITZ_TIMERS.playMs)} play
            </td>
          </tr>
          <tr>
            <td>Three timeouts in a row</td>
            <td>a bot takes the seat; it hands back the moment you do anything</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function RulesSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} title="How to play" onClose={onClose} testId="rules">
      <RulesContent />
    </Sheet>
  );
}
